/**
 * Assembly mode's work order pieces: the first question (Work order, Repair,
 * or the pre-work-order flow kept for the transition), the run choice or the repair's model, the strip that names either
 * for the session, the final check, and the Flag sheet. AssemblyWorkflow owns the state; these only
 * render it.
 *
 * Built on the surface tokens, not the scanner's older gray-border look, so the
 * file sits in m3-surfaces.test.ts. The handheld rules apply throughout: every
 * key is at least 44px tall, and every field submits on Enter.
 */

import React, { useEffect, useState } from 'react';
import { FOCUS, MOTION, STATE_LAYER } from '../../Requisitions/buttonStyles';
import { modalInput } from '../../components/review/fieldStyles';
import { variantLabel, type ProductSummary } from '../../Parts/bomsApi';
import { BuiltRing } from '../../WorkOrders/WorkOrderBits';
import {
  componentReading,
  type FloorRun,
  type UnitCheck,
} from '../../WorkOrders/workOrdersApi';

// ---------------------------------------------------------------------------
// Keys, sized for a thumb
// ---------------------------------------------------------------------------

const KEY = `inline-flex items-center justify-center gap-2 rounded-full font-semibold disabled:opacity-40 ${FOCUS} ${MOTION}`;
/** The one filled key on a screen. */
export const KEY_PRIMARY = `${KEY} min-h-[3.5rem] w-full bg-primary px-6 text-lg text-on-primary hover:bg-primary/90`;
export const KEY_TONAL = `${KEY} min-h-[3rem] w-full bg-surface-container-highest px-5 text-base text-on-surface ${STATE_LAYER}`;
export const KEY_QUIET = `${KEY} min-h-[2.75rem] px-4 text-sm font-medium text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface`;

/** The run's build as a word: an untagged run builds the standard drone. */
export const buildLabel = (variant: string | null) => variantLabel(variant, 'Standard');

const modelLine = (r: Pick<FloorRun, 'model_code' | 'model_name'>) =>
  r.model_name ? `${r.model_code}, ${r.model_name}` : r.model_code;

// ---------------------------------------------------------------------------
// Glyphs: filled paths, one per state, so a state reads by shape as well as tone
// ---------------------------------------------------------------------------

const FittedGlyph = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5 shrink-0 text-emerald-400">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm-1.4 11.2L5.4 10l1.2-1.2 2 2 4.8-4.8 1.2 1.2-6 6Z"
    />
  </svg>
);

const MissingGlyph = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5 shrink-0 text-orange-300">
    <path fill="currentColor" fillRule="evenodd" d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16Zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12Z" />
  </svg>
);

const ShortGlyph = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-5 w-5 shrink-0 text-orange-300">
    <path fill="currentColor" fillRule="evenodd" d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16ZM6 9h8v2H6V9Z" />
  </svg>
);

// ---------------------------------------------------------------------------
// The first question
// ---------------------------------------------------------------------------

const CHOICE = `flex w-full min-h-[7rem] flex-col items-start justify-center gap-1 rounded-xl bg-surface-container-high px-5 py-4 text-left ${STATE_LAYER} ${FOCUS} ${MOTION}`;

// The transition mode sits a step down in tone and size: it is there for whoever
// still needs it, not offered as an equal choice.
const CHOICE_QUIET = `mt-2 flex w-full min-h-[3.5rem] flex-col items-start justify-center gap-0.5 rounded-xl bg-surface-container-low px-5 py-3 text-left ${STATE_LAYER} ${FOCUS} ${MOTION}`;

export const ModeChoice: React.FC<{ onWorkOrder: () => void; onRepair: () => void; onLegacy: () => void }> = ({
  onWorkOrder,
  onRepair,
  onLegacy,
}) => (
  <div className="mx-auto flex w-full max-w-sm flex-col gap-3 px-4 py-6">
    <h2 className="text-lg font-semibold text-on-surface">What are you building for?</h2>
    <button type="button" onClick={onWorkOrder} className={CHOICE}>
      <span className="text-xl font-semibold text-on-surface">Work order</span>
      <span className="text-sm text-on-surface-variant">Build drones for a run on the floor.</span>
    </button>
    <button type="button" onClick={onRepair} className={CHOICE}>
      <span className="text-xl font-semibold text-on-surface">Repair</span>
      <span className="text-sm text-on-surface-variant">Fit or swap parts on any drone. No run.</span>
    </button>
    <button type="button" onClick={onLegacy} className={CHOICE_QUIET}>
      <span className="text-base font-semibold text-on-surface-variant">This will go away</span>
      <span className="text-sm text-on-surface-variant">The assembly flow from before work orders. Kept while the shop switches over.</span>
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// The run choice
// ---------------------------------------------------------------------------

const RunFacts: React.FC<{ run: FloorRun }> = ({ run }) => (
  <>
    <span className="min-w-0 flex-1">
      <span className="block text-lg font-semibold tabular-nums text-on-surface">{run.number}</span>
      <span className="block truncate text-sm text-on-surface-variant">{modelLine(run)}</span>
      <span className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center rounded-full bg-surface-container-highest px-2.5 py-0.5 text-xs font-medium text-on-surface">
          {buildLabel(run.variant)}
        </span>
        {!!run.open_pick_requests && (
          <span className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary">
            Box requested
          </span>
        )}
      </span>
    </span>
    <span className="flex shrink-0 items-center gap-2 text-sm tabular-nums text-on-surface">
      <BuiltRing done={run.completed_count} total={run.quantity} size={28} />
      <span>
        {run.completed_count} of {run.quantity}
        <span className="text-on-surface-variant"> built</span>
      </span>
    </span>
  </>
);

interface RunChoiceProps {
  runs: FloorRun[] | null;
  error: string | null;
  onRetry: () => void;
  onPick: (run: FloorRun) => void;
  onBack: () => void;
  onRepair: () => void;
}

export const RunChoice: React.FC<RunChoiceProps> = ({ runs, error, onRetry, onPick, onBack, onRepair }) => {
  let body: React.ReactNode;
  if (error) {
    body = (
      <div role="alert" className="rounded-xl bg-surface-container-low p-4 text-sm">
        <p className="text-on-surface">{error}</p>
        <button type="button" onClick={onRetry} className={`mt-3 ${KEY_TONAL}`}>
          Try again
        </button>
      </div>
    );
  } else if (!runs) {
    body = (
      <div className="space-y-2" aria-busy="true" aria-label="Loading the runs on the floor">
        {[0, 1].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-surface-container-low motion-safe:animate-pulse" />
        ))}
      </div>
    );
  } else if (runs.length === 0) {
    body = (
      <div className="rounded-xl bg-surface-container-low p-4">
        <p className="text-base text-on-surface">No runs on the floor. Release one on the work order page.</p>
        <button type="button" onClick={onRepair} className={`mt-3 ${KEY_TONAL}`}>
          Repair a drone instead
        </button>
      </div>
    );
  } else if (runs.length === 1) {
    const run = runs[0];
    body = (
      <div className="rounded-xl bg-surface-container-high p-4">
        <div className="flex items-start gap-3">
          <RunFacts run={run} />
        </div>
        <span title={`Build drones for ${run.number}`} className="mt-4 flex">
          <button type="button" onClick={() => onPick(run)} className={KEY_PRIMARY}>
            Continue
          </button>
        </span>
      </div>
    );
  } else {
    body = (
      <ul className="space-y-2">
        {runs.map((run) => (
          <li key={run.id}>
            <button
              type="button"
              onClick={() => onPick(run)}
              aria-label={`${run.number}, ${modelLine(run)}, ${buildLabel(run.variant)}, ${run.completed_count} of ${run.quantity} built`}
              className={`flex w-full min-h-[5.5rem] items-center gap-3 rounded-xl bg-surface-container-high px-4 py-3 text-left ${STATE_LAYER} ${FOCUS} ${MOTION}`}
            >
              <RunFacts run={run} />
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col gap-3 px-4 py-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-on-surface">
          {runs && runs.length > 1 ? 'Which run are you building?' : 'The run on the floor'}
        </h2>
        <button type="button" onClick={onBack} className={KEY_QUIET}>
          Back
        </button>
      </div>
      {body}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The repair's model
// ---------------------------------------------------------------------------

/** A model the repair bench can lay out: one with a current parts list. */
export type RepairModel = Pick<ProductSummary, 'name' | 'display_name' | 'description'> & { model_code: string };

const repairModelName = (m: RepairModel) => m.display_name || m.name || m.description || null;

interface RepairModelChoiceProps {
  models: RepairModel[] | null;
  error: string | null;
  onRetry: () => void;
  onPick: (model: RepairModel) => void;
  onBack: () => void;
}

export const RepairModelChoice: React.FC<RepairModelChoiceProps> = ({ models, error, onRetry, onPick, onBack }) => {
  let body: React.ReactNode;
  if (error) {
    body = (
      <div role="alert" className="rounded-xl bg-surface-container-low p-4 text-sm">
        <p className="text-on-surface">{error}</p>
        <button type="button" onClick={onRetry} className={`mt-3 ${KEY_TONAL}`}>
          Try again
        </button>
      </div>
    );
  } else if (!models) {
    body = (
      <div className="space-y-2" aria-busy="true" aria-label="Loading the drone models">
        {[0, 1].map((i) => (
          <div key={i} className="h-[4.5rem] rounded-xl bg-surface-container-low motion-safe:animate-pulse" />
        ))}
      </div>
    );
  } else if (models.length === 0) {
    body = (
      <div className="rounded-xl bg-surface-container-low p-4">
        <p className="text-base text-on-surface">
          No model has a parts list yet, so the bench has no slots to show. Start one on the Product BOMs page.
        </p>
      </div>
    );
  } else {
    body = (
      <ul className="space-y-2">
        {models.map((m) => {
          const name = repairModelName(m);
          return (
            <li key={m.model_code}>
              <button
                type="button"
                onClick={() => onPick(m)}
                aria-label={name ? `${m.model_code}, ${name}` : m.model_code}
                className={`flex w-full min-h-[4.5rem] flex-col items-start justify-center rounded-xl bg-surface-container-high px-4 py-3 text-left ${STATE_LAYER} ${FOCUS} ${MOTION}`}
              >
                <span className="block text-lg font-semibold tabular-nums text-on-surface">{m.model_code}</span>
                {name && <span className="block w-full truncate text-sm text-on-surface-variant">{name}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-sm flex-col gap-3 px-4 py-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-on-surface">Which model are you repairing?</h2>
        <button type="button" onClick={onBack} className={KEY_QUIET}>
          Back
        </button>
      </div>
      {body}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The strip that names the session's run, or the repair's model
// ---------------------------------------------------------------------------

export const RunStrip: React.FC<{
  run: FloorRun | null;
  repairModel?: RepairModel | null;
  /** The pre-work-order flow: no run and no model to name. */
  legacy?: boolean;
  onChange: () => void;
}> = ({ run, repairModel = null, legacy = false, onChange }) => (
  <div className="flex items-center gap-3 rounded-xl bg-surface-container-low py-2 pl-4 pr-1">
    {legacy ? (
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-on-surface">This will go away</span>
    ) : run ? (
      <>
        <BuiltRing done={run.completed_count} total={run.quantity} size={24} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold tabular-nums text-on-surface">
            {run.number}, {buildLabel(run.variant)}
          </span>
          <span className="block truncate text-xs tabular-nums text-on-surface-variant">
            {run.model_code}, {run.completed_count} of {run.quantity} built
          </span>
        </span>
      </>
    ) : (
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold tabular-nums text-on-surface">
          {repairModel ? `Repair, ${repairModel.model_code}` : 'Repair, no run'}
        </span>
        {repairModel && repairModelName(repairModel) && (
          <span className="block truncate text-xs text-on-surface-variant">{repairModelName(repairModel)}</span>
        )}
      </span>
    )}
    <span
      title={legacy ? 'Back to the choice of work order or repair' : run ? 'Pick another run, or switch to repair' : 'Pick another model, or switch to a run'}
      className="inline-flex"
    >
      <button type="button" onClick={onChange} className={KEY_QUIET}>
        Change
      </button>
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// The final check
// ---------------------------------------------------------------------------

interface FinalCheckProps {
  check: UnitCheck;
  /** The slot's label from the model's parts list, when the line names a slot. */
  slotLabel?: (key: string) => string | undefined;
  serial: string;
  busy: 'built' | 'request' | null;
  onBuilt: () => void;
  onRequestBox: () => void;
  onBack: () => void;
}

export const FinalCheck: React.FC<FinalCheckProps> = ({
  check,
  slotLabel,
  serial,
  busy,
  onBuilt,
  onRequestBox,
  onBack,
}) => {
  const missing = check.traced.filter((t) => !t.fitted).length;
  const canRequest = check.short.length > 0 || check.traced.some((t) => !t.fitted && t.in_assembly === 0);
  return (
    <section aria-labelledby="final-check-title" className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="final-check-title" className="text-lg font-semibold text-on-surface">
          Final check
        </h2>
        <span className="font-mono text-sm tabular-nums text-on-surface-variant">{serial}</span>
      </div>

      {check.traced.length > 0 && (
        <ul aria-label="Scanned parts" className="divide-y divide-outline-variant rounded-xl bg-surface-container-low">
          {check.traced.map((t) => {
            const reading = componentReading(t.component);
            return (
              <li key={t.line_id} className="flex items-start gap-3 px-4 py-3">
                {t.fitted ? <FittedGlyph /> : <MissingGlyph />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base text-on-surface">
                    {(t.slot && slotLabel?.(t.slot)) || t.target_name}
                  </span>
                  {t.fitted ? (
                    <span className="block truncate font-mono text-xs tabular-nums text-on-surface-variant">
                      {reading || 'Fitted'}
                    </span>
                  ) : (
                    <span className="block text-sm text-orange-300">
                      {t.in_assembly === 0 ? 'None in Assembly to scan. Request a box.' : 'Missing. Scan it onto the drone first.'}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {check.short.length > 0 && (
        <ul aria-label="Parts Assembly is short of" className="divide-y divide-outline-variant rounded-xl bg-surface-container-low">
          {check.short.map((s) => (
            <li key={s.line_id} className="flex items-start gap-3 px-4 py-3">
              <ShortGlyph />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base text-on-surface">{s.target_name}</span>
                <span className="block text-sm tabular-nums text-orange-300">
                  Needs {s.need}, Assembly has {s.in_assembly}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {check.ready ? (
        <span title="Finish this drone. Its parts come out of Assembly." className="flex">
          <button type="button" onClick={onBuilt} disabled={busy !== null} className={KEY_PRIMARY}>
            {busy === 'built' ? 'Recording…' : 'Built'}
          </button>
        </span>
      ) : (
        <>
          {check.traced.some((t) => !t.fitted && t.in_assembly !== 0) && (
            <p className="text-sm text-on-surface-variant">
              Scan the missing {missing === 1 ? 'part' : 'parts'} and the slots come back.
            </p>
          )}
          {canRequest && (
            <span title="Ask for a box of the short parts. A manager brings it." className="flex">
              <button type="button" onClick={onRequestBox} disabled={busy !== null} className={KEY_TONAL}>
                {busy === 'request' ? 'Requesting…' : 'Request a box'}
              </button>
            </span>
          )}
        </>
      )}

      <button type="button" onClick={onBack} disabled={busy !== null} className={`self-start ${KEY_QUIET}`}>
        Back to the slots
      </button>
    </section>
  );
};

// ---------------------------------------------------------------------------
// After the check: built, or set aside
// ---------------------------------------------------------------------------

/**
 * The station's one moment: the check draws itself when a drone is built. It
 * is a stroke because it is a line drawing itself, the house exception.
 */
export const BuiltLanding: React.FC<{ serial: string; run: FloorRun; onNext: () => void }> = ({
  serial,
  run,
  onNext,
}) => {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div role="status" className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <svg viewBox="0 0 48 48" fill="none" aria-hidden="true" className="h-16 w-16 text-emerald-400">
        <path
          d="M12 25l8 8 16-17"
          stroke="currentColor"
          strokeWidth={4}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={40}
          strokeDashoffset={drawn ? 0 : 40}
          className="transition-[stroke-dashoffset] duration-300 ease-emphasized motion-reduce:transition-none"
        />
      </svg>
      <p className="text-xl font-semibold text-on-surface">Built</p>
      <p className="font-mono text-sm tabular-nums text-on-surface-variant">{serial}</p>
      <p className="text-sm tabular-nums text-on-surface-variant">
        {run.completed_count} of {run.quantity} built on {run.number}
      </p>
      <button type="button" onClick={onNext} className={`mt-2 ${KEY_TONAL}`}>
        Scan the next drone
      </button>
    </div>
  );
};

export const RequestedLanding: React.FC<{ serial: string; onNext: () => void }> = ({ serial, onNext }) => (
  <div role="status" className="flex flex-col items-center gap-3 px-6 py-12 text-center">
    <p className="text-xl font-semibold text-on-surface">Requested. A manager will bring a box.</p>
    <p className="text-sm text-on-surface-variant">
      Set <span className="font-mono tabular-nums">{serial}</span> aside. Check it again once the box is in Assembly.
    </p>
    <button type="button" onClick={onNext} className={`mt-2 ${KEY_TONAL}`}>
      Scan the next drone
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// Flag
// ---------------------------------------------------------------------------

export const FlagKey: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <span title="This drone failed. Take it off the run and set it aside for repair." className="inline-flex">
    <button type="button" onClick={onClick} className={KEY_QUIET}>
      Flag
    </button>
  </span>
);

export const FlagSheet: React.FC<{
  /** The drone serial, or the core hex for a unit that has no serial yet. */
  identity: string;
  busy: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}> = ({ identity, busy, onSubmit, onCancel }) => {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const submit = () => {
    if (trimmed && !busy) onSubmit(trimmed);
  };
  return (
    <section aria-labelledby="flag-title" className="flex flex-col gap-3 rounded-xl bg-surface-container-low p-4">
      <h2 id="flag-title" className="text-lg font-semibold text-on-surface">
        Flag <span className="font-mono tabular-nums">{identity}</span>
      </h2>
      <p className="text-sm text-on-surface-variant">
        Finish fitting its parts first. Flagging charges this drone a full set from Assembly, screws and
        motors included, then it comes off the run and goes to repair.
      </p>
      <label className="block">
        <span className="mb-1 block text-sm text-on-surface-variant">What is wrong with it?</span>
        <input
          type="text"
          autoFocus
          autoComplete="off"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Motor 3 does not spin"
          className={`m-0 min-h-[2.75rem] ${modalInput}`}
        />
      </label>
      <div className="flex items-center gap-2">
        <button type="button" onClick={submit} disabled={!trimmed || busy} className={`flex-1 ${KEY_TONAL}`}>
          {busy ? 'Flagging…' : 'Flag it'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={KEY_QUIET}>
          Cancel
        </button>
      </div>
    </section>
  );
};
